import { Button, Dropdown, DropdownButton } from "react-bootstrap";
import { Link } from "react-router-dom";
import { CarTireSetModel } from "../../../domain/models/carTireSetModel";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useAuthorization } from "../../../../../hooks/useAuthorization";
import { CarModel } from "../../../domain/models/carModel";

export const CarTireSetsFormPartRequest = ({
  tireSet,
  car,
  licensePlate,
  isFreestock = false,
}: {
  tireSet: CarTireSetModel;
  car: CarModel | undefined;
  licensePlate?: string;
  isFreestock?: boolean;
}) => {
  const { t } = useTranslation("common");
  const { hasRole } = useAuthorization();

  const requests = useMemo(() => {
    return tireSet.requests?.filter(
      (request) =>
        request.state !== "delivered" &&
        request.state !== "cancelled" &&
        request.state !== "in_storage" &&
        request.requestTires?.length !== 0,
    );
  }, [tireSet]);

  const requestExists = useMemo(() => {
    return (
      !isFreestock ||
      tireSet.storedLatestRequest === undefined ||
      (requests !== undefined && requests.length === 0)
    );
  }, [tireSet, isFreestock, requests]);

  // Dealer is not allowed to make a pickup request if one of the sets is in hotel.
  // Only if there is a deliver request.
  const isRequestAllowed = useMemo(() => {
    const requestType = tireSet.inHotel ? "deliver" : "pickup";
    if (requestType == "deliver") return true;

    let hasDeliverRequest = false;
    let hasSetInHotel = false;

    car?.tireSets?.forEach((set) => {
      if (set.inHotel) hasSetInHotel = true;

      set.activeRequests?.forEach((request) => {
        if (request.type == "deliver") {
          hasDeliverRequest = true;
        }
      });
    });

    return hasSetInHotel ? hasDeliverRequest : true;
  }, [tireSet, requests]);

  if (requestExists) {
    if (tireSet.activeRequests && tireSet.activeRequests.length > 1) {
      return (
        <DropdownButton
          title={t("request.edit_button.multiple")}
          id="dropdown-menu-align-end"
          size="sm"
          className="d-inline"
          variant={"success"}
        >
          {tireSet.activeRequests?.map((request) => {
            const date = request.pickupAndDeliveryDate
              ? new Date(request.pickupAndDeliveryDate)
              : new Date();
            const formattedDate = new Intl.DateTimeFormat("en-GB")
              .format(date)
              .replace(/\//g, "-");

            return (
              <Link
                to={`/requests/${request.id}`}
                key={request.id}
                className="dropdown-item"
                state={{
                  licencePlate: licensePlate,
                  tireType: tireSet.type,
                }}
              >
                {formattedDate} {t(`request.edit_button.${request.type}`)}
              </Link>
            );
          })}
        </DropdownButton>
      );
    }

    return (
      <Link
        to={
          tireSet.storedLatestRequest &&
          tireSet.storedLatestRequest.state !== "delivered" &&
          tireSet.storedLatestRequest.state !== "cancelled" &&
          tireSet.storedLatestRequest.state !== "in_storage"
            ? `/requests/${tireSet.storedLatestRequest.id}`
            : `/tire-sets/${tireSet.id}/create-request`
        }
        state={{
          licencePlate: licensePlate,
          tireType: tireSet.type,
        }}
      >
        {!(
          hasRole("dealer-employee") &&
          tireSet.storedLatestRequest?.state != "concept" &&
          tireSet.storedLatestRequest?.state != "submitted"
        ) &&
        tireSet.storedLatestRequest &&
        tireSet.storedLatestRequest.state !== "delivered" &&
        tireSet.storedLatestRequest.state !== "cancelled" &&
        tireSet.storedLatestRequest.state !== "in_storage" ? (
          <Button size="sm" variant={"success"}>
            {t(`request.edit_button.${tireSet.storedLatestRequest.type}`)}
          </Button>
        ) : (
          <>
            {!hasRole("dbh-employee") &&
              !hasRole("dbh-driver") &&
              !hasRole("dbh-order-picker") &&
              !hasRole("dealer-employee") &&
              isRequestAllowed && (
                <Button size="sm">{t("request.add_button")}</Button>
              )}
          </>
        )}
      </Link>
    );
  }

  const type = requests && requests?.length > 0 ? requests[0].type : "pickup";

  return (
    <DropdownButton
      title={t(type + "_requests")}
      id="dropdown-menu-align-end"
      size="sm"
      className="d-inline"
    >
      {requests?.map((request) => (
        <Link
          to={`/requests/${request.id}`}
          key={request.id}
          className="dropdown-item"
          state={{
            licencePlate: licensePlate,
            tireType: tireSet.type,
          }}
        >
          {request.requestTires?.map((tire, index, array) => (
            <>
              {t("tire.short." + tire.tire.car_position)}
              {index < array.length - 2 && ", "}
              {index === array.length - 2 && ` ${t("and")} `}
            </>
          ))}
        </Link>
      ))}

      <Dropdown.Divider />
      {isRequestAllowed && (
        <Link
          to={`/tire-sets/${tireSet.id}/create-request`}
          className="dropdown-item"
          state={{
            licencePlate: licensePlate,
            tireType: tireSet.type,
          }}
        >
          + {t("request.add_button")}
        </Link>
      )}
    </DropdownButton>
  );
};
